import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { EventControl } from './EventControl';
import { SignupSearch } from './SignupSearch';
import { Reports } from './Reports.jsx';
import { DrawManager } from './DrawManager';


// Toast component for success/error messages
function Toast({ message, type = 'success', onClose }) {
    React.useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    const bgColor = type === 'success' ? 'bg-green-500/10' : 'bg-destructive/10';
    const borderColor = type === 'success' ? 'border-green-500/50' : 'border-destructive/50';
    const textColor = type === 'success' ? 'text-green-600' : 'text-destructive';
    const icon = type === 'success' ? '✅' : '⚠️';

    return (
        <div className={`rounded-lg border ${borderColor} ${bgColor} p-4 fixed bottom-4 right-4 z-50 animate-in fade-in slide-in-from-bottom-5`}>
            <div className="flex items-center space-x-2">
                <span className={textColor}>{icon}</span>
                <p className={`text-sm ${textColor}`}>{message}</p>
            </div>
        </div>
    );
}

// A component to list all events
function EventList({ api }) {
    const { data: events, isLoading, error } = useQuery({
        queryKey: ['events'],
        queryFn: () => api.getAllEvents(),
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                <div className="flex items-center space-x-2">
                    <span className="text-destructive">⚠️</span>
                    <p className="text-sm text-destructive">Failed to load events: {error.message}</p>
                </div>
            </div>
        );
    }

    if (!events?.length) {
        return (
            <div className="text-center py-8">
                <p className="text-muted-foreground">No events found. Create your first event to get started!</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold">Your Events</h3>
            <div className="grid gap-4">
                {events.map((event) => (
                    <Link
                        key={event.id}
                        to={`/producer/event/${event.id}`}
                        className="block p-6 rounded-lg border bg-card hover:border-primary transition-colors"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h4 className="font-medium">{event.name}</h4>
                                <p className="text-sm text-muted-foreground">Created: {new Date(event.createdAt).toLocaleDateString()}</p>
                            </div>
                            <div className="text-muted-foreground">→</div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

// Participant Manager Component
function ParticipantManager({ api, eventId, classId, onSuccess }) {
    const [participantData, setParticipantData] = useState({ name: '', homeTown: '', horseName: '' });
    const [confirmDelete, setConfirmDelete] = useState(null);
    const queryClient = useQueryClient();

    // Fetch participants for the class
    const { data: participants, isLoading } = useQuery({
        queryKey: ['participants', classId],
        queryFn: () => api.getParticipants(classId),
        enabled: !!classId,
    });

    // Create participant mutation
    const createParticipantMutation = useMutation({
        mutationFn: async () => {
            // First create the participant
            const participantId = await api.createParticipant({
                name: participantData.name,
                homeTown: participantData.homeTown,
                horseName: participantData.horseName,
            });

            // Then add their horse
            await api.addHorseToParticipant(participantId, participantData.horseName);

            // Finally, add them to the class
            await api.addParticipantToClass(classId, participantId);

            return participantId;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['participants', classId]);
            queryClient.invalidateQueries(['event', eventId]);
            queryClient.invalidateQueries(['eventStatus']);
            setParticipantData({ name: '', homeTown: '', horseName: '' });
            onSuccess?.('Participant added successfully!');
        },
    });

    // Delete participant mutation
    const deleteParticipantMutation = useMutation({
        mutationFn: async (participantId) => {
            // Remove participant from all classes in the event
            await api.removeParticipantFromEvent(eventId, participantId);
            // Delete the participant
            await api.deleteParticipant(participantId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['participants', classId]);
            queryClient.invalidateQueries(['event', eventId]);
            queryClient.invalidateQueries(['eventStatus']);
            setConfirmDelete(null);
            onSuccess?.('Participant removed successfully!');
        },
        onError: (error) => {
            onSuccess?.(`Failed to remove participant: ${error.message}`, 'error');
            setConfirmDelete(null);
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!participantData.name || !participantData.horseName) return;
        createParticipantMutation.mutate();
    };

    const handleDelete = (participant) => {
        setConfirmDelete(participant);
    };

    const confirmDeleteParticipant = () => {
        if (confirmDelete) {
            deleteParticipantMutation.mutate(confirmDelete.id);
        }
    };

    if (!classId) {
        return (
            <div className="text-center py-4">
                <p className="text-muted-foreground">Select a class to manage participants</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Confirmation Dialog */}
            {confirmDelete && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-2">Confirm Removal</h3>
                        <p className="text-muted-foreground mb-4">
                            Are you sure you want to remove {confirmDelete.name} from this event?
                            This will remove them from all registered classes and cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => setConfirmDelete(null)}
                                className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteParticipant}
                                disabled={deleteParticipantMutation.isPending}
                                className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700"
                            >
                                {deleteParticipantMutation.isPending ? 'Removing...' : 'Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Participant Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                        <label htmlFor="name" className="text-sm font-medium text-foreground">
                            Participant Name
                        </label>
                        <input
                            id="name"
                            value={participantData.name}
                            onChange={e => setParticipantData({ ...participantData, name: e.target.value })}
                            placeholder="e.g., John Doe"
                            disabled={createParticipantMutation.isPending}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                    </div>
                    <div className="space-y-2">
                        <label htmlFor="homeTown" className="text-sm font-medium text-foreground">
                            Home Town
                        </label>
                        <input
                            id="homeTown"
                            value={participantData.homeTown}
                            onChange={e => setParticipantData({ ...participantData, homeTown: e.target.value })}
                            placeholder="e.g., Austin, TX"
                            disabled={createParticipantMutation.isPending}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                    </div>
                    <div className="space-y-2">
                        <label htmlFor="horseName" className="text-sm font-medium text-foreground">
                            Horse Name
                        </label>
                        <input
                            id="horseName"
                            value={participantData.horseName}
                            onChange={e => setParticipantData({ ...participantData, horseName: e.target.value })}
                            placeholder="e.g., Lightning"
                            disabled={createParticipantMutation.isPending}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                    </div>
                </div>
                <button
                    type="submit"
                    disabled={createParticipantMutation.isPending || !participantData.name || !participantData.horseName}
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                >
                    {createParticipantMutation.isPending ? (
                        <>
                            <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Adding...
                        </>
                    ) : (
                        'Add Participant'
                    )}
                </button>
            </form>

            {/* Participants List */}
            {isLoading ? (
                <div className="flex items-center justify-center p-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
            ) : participants?.length > 0 ? (
                <div className="space-y-2">
                    {participants.map((participant) => (
                        <div
                            key={participant.id}
                            className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white"
                        >
                            <div>
                                <h4 className="font-medium text-foreground">{participant.name}</h4>
                                <p className="text-sm text-muted-foreground">
                                    {[participant.homeTown, participant.horses?.[0]].filter(Boolean).join(' • ')}
                                </p>
                            </div>
                            <button
                                onClick={() => handleDelete(participant)}
                                className="px-3 py-1 rounded text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-4">
                    <p className="text-muted-foreground">No participants in this class yet</p>
                </div>
            )}
        </div>
    );
}

// Pre-Event Phase Component
function PreEventPhase({
    event,
    eventId,
    eventStateId,
    classes,
    isLoadingClasses,
    className,
    setClassName,
    divisions,
    setDivisions,
    entryFee,
    setEntryFee,
    copySuccess,
    copySpectatorUrl,
    handleCreateClass,
    handleDivisionChange,
    createClassMutation,
    api,
    setToast
}) {
    const [classManagementModal, setClassManagementModal] = useState({ isOpen: false, classInfo: null });
    return (
        <div className="space-y-6">
            {/* Quick Links Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Live View URL */}
                <div className="rounded-lg border bg-card p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                            <div className="h-6 w-6 rounded-full bg-purple-500/10 flex items-center justify-center">
                                <span className="text-purple-600 text-sm">👁️</span>
                            </div>
                            <h3 className="text-lg font-semibold text-foreground">Live View URL</h3>
                        </div>
                        <button
                            onClick={copySpectatorUrl}
                            disabled={!eventStateId}
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2"
                        >
                            {copySuccess ? (
                                <span className="flex items-center">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Copied!
                                </span>
                            ) : (
                                <span className="flex items-center">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                    </svg>
                                    Copy URL
                                </span>
                            )}
                        </button>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                        Share this URL with spectators to let them view the live event.
                    </p>
                    <div className="p-2 bg-muted rounded text-sm font-mono break-all mb-4">
                        {eventStateId ? `${window.location.origin}/spectator/${eventStateId}` : 'Loading...'}
                    </div>
                    {eventStateId && (
                        <div className="flex flex-col items-center space-y-2 mt-4 p-4 bg-white rounded-lg border">
                            <QRCodeSVG
                                value={`${window.location.origin}/spectator/${eventStateId}`}
                                size={150}
                                level="H"
                                includeMargin={true}
                                imageSettings={{
                                    src: "/favicon.ico",
                                    x: undefined,
                                    y: undefined,
                                    height: 20,
                                    width: 20,
                                    excavate: true,
                                }}
                            />
                            <p className="text-sm text-muted-foreground text-center">
                                Scan to watch live
                            </p>
                        </div>
                    )}
                </div>

                {/* Public Signup URL */}
                <div className="rounded-lg border bg-card p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                            <div className="h-6 w-6 rounded-full bg-green-500/10 flex items-center justify-center">
                                <span className="text-green-600 text-sm">🏇</span>
                            </div>
                            <h3 className="text-lg font-semibold text-foreground">Public Signup URL</h3>
                        </div>
                        <button
                            onClick={() => {
                                const signupUrl = `${window.location.origin}/signup/${eventId}`;
                                navigator.clipboard.writeText(signupUrl).then(() => {
                                    setToast({ message: 'Signup URL copied to clipboard!', type: 'success' });
                                });
                            }}
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-green-600 text-white hover:bg-green-700 h-9 px-4 py-2"
                        >
                            <span className="flex items-center">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                </svg>
                                Copy URL
                            </span>
                        </button>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                        Share this URL with riders so they can sign up for classes.
                    </p>
                    <div className="p-2 bg-muted rounded text-sm font-mono break-all">
                        {`${window.location.origin}/signup/${eventId}`}
                    </div>
                </div>
            </div>

            {/* Classes Management */}
            <div className="rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-2">
                        <div className="h-6 w-6 rounded-full bg-blue-500/10 flex items-center justify-center">
                            <span className="text-blue-600 text-sm">🏆</span>
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">Classes</h3>
                    </div>
                    <span className="text-sm text-muted-foreground">
                        {classes?.length || 0} classes created
                    </span>
                </div>

                {/* Classes List */}
                {isLoadingClasses ? (
                    <div className="flex items-center justify-center p-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                ) : classes?.length > 0 ? (
                    <div className="grid gap-3 mb-6">
                        {classes.map((classInfo, index) => (
                            <div
                                key={classInfo.id}
                                className="w-full flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-white"
                            >
                                <div className="flex items-center space-x-3">
                                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-600">
                                        {index + 1}
                                    </div>
                                    <div className="flex flex-col items-start">
                                        <span className="font-medium text-foreground">{classInfo.name}</span>
                                        {(() => {
                                            const fee = Array.isArray(classInfo.entryFee) ? classInfo.entryFee[1] || 0 : classInfo.entryFee;
                                            return fee > 0 ? (
                                                <span className="text-sm text-green-600 font-medium">
                                                    ${fee.toFixed(2)} entry fee
                                                </span>
                                            ) : null;
                                        })()}
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="text-sm text-muted-foreground">
                                        {classInfo.participantCount || 0} participants
                                    </div>
                                    <button
                                        onClick={() => setClassManagementModal({ isOpen: true, classInfo })}
                                        className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-3 py-1"
                                    >
                                        Manage
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg mb-6">
                        <div className="text-gray-400 mb-2">🏆</div>
                        <p className="text-muted-foreground">No classes created yet</p>
                        <p className="text-sm text-muted-foreground">Create your first class below</p>
                    </div>
                )}

                {/* Create Class Form */}
                <div className="border-t pt-6">
                    <h4 className="text-md font-semibold mb-4">Create New Class</h4>
                    <form onSubmit={handleCreateClass} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label htmlFor="className" className="text-sm font-medium text-foreground">
                                    Class Name
                                </label>
                                <input
                                    id="className"
                                    type="text"
                                    value={className}
                                    onChange={e => setClassName(e.target.value)}
                                    placeholder="e.g., Open 4D, Youth 1D, Senior 2D"
                                    disabled={createClassMutation.isPending}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                />
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="entry-fee" className="text-sm font-medium">
                                    Entry Fee ($)
                                </label>
                                <input
                                    type="number"
                                    id="entry-fee"
                                    min="0"
                                    step="0.01"
                                    value={entryFee}
                                    onChange={(e) => setEntryFee(e.target.value)}
                                    placeholder="0.00"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">
                                Division Settings
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {/* Always show 1D as enabled, no split input */}
                                <div className="flex items-center space-x-3 p-3 border rounded-lg">
                                    <input
                                        type="checkbox"
                                        id="division-1D"
                                        checked={true}
                                        disabled
                                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                    <label htmlFor="division-1D" className="text-sm font-medium flex-1">
                                        1D
                                    </label>
                                    <span className="text-xs text-muted-foreground">Fastest</span>
                                </div>
                                {/* Show split inputs for 2D and higher */}
                                {divisions.filter(division => division.id !== '1D').map((division) => (
                                    <div key={division.id} className="flex items-center space-x-3 p-3 border rounded-lg">
                                        <input
                                            type="checkbox"
                                            id={`division-${division.id}`}
                                            checked={division.enabled}
                                            onChange={(e) => handleDivisionChange(division.id, 'enabled', e.target.checked)}
                                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <label htmlFor={`division-${division.id}`} className="text-sm font-medium flex-1">
                                            {division.id}
                                        </label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={division.timeSplit}
                                            onChange={(e) => handleDivisionChange(division.id, 'timeSplit', parseFloat(e.target.value))}
                                            disabled={!division.enabled}
                                            placeholder="+0.0s"
                                            className="flex h-8 w-16 rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        />
                                    </div>
                                ))}
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Time splits are added to the fastest time to determine division breaks.
                            </p>
                        </div>

                        <button
                            type="submit"
                            disabled={createClassMutation.isPending || !className || !divisions.some(d => d.enabled) || entryFee < 0}
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                        >
                            {createClassMutation.isPending ? (
                                <>
                                    <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Creating...
                                </>
                            ) : (
                                'Create Class'
                            )}
                        </button>
                    </form>
                </div>
            </div>

            {/* Signup Search */}
            <div className="rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                    <div className="h-6 w-6 rounded-full bg-green-500/10 flex items-center justify-center">
                        <span className="text-green-600 text-sm">🔍</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">Signup Management</h3>
                </div>

                <SignupSearch api={api} />
            </div>

            {/* Class Management Modal */}
            <ClassManagementModal
                isOpen={classManagementModal.isOpen}
                onClose={() => setClassManagementModal({ isOpen: false, classInfo: null })}
                classInfo={classManagementModal.classInfo}
                api={api}
                eventId={eventId}
                onSuccess={(message, type = 'success') => setToast({ message, type })}
            />
        </div>
    );
}

// During Event Phase Component
function DuringEventPhase({ event, eventId, eventStateId, eventStatus, api, setToast }) {
    return (
        <div className="space-y-6">
            {/* Event Control */}
            {eventStateId && (
                <div className="rounded-lg border bg-card p-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <div className="h-6 w-6 rounded-full bg-red-500/10 flex items-center justify-center">
                            <span className="text-red-600 text-sm">🎮</span>
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">Event Control</h3>
                    </div>

                    <EventControl api={api} eventId={eventId} eventStateId={eventStateId} />
                </div>
            )}

            {/* Presenter Mode */}
            <div className="rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                        <div className="h-6 w-6 rounded-full bg-green-500/10 flex items-center justify-center">
                            <span className="text-green-600 text-sm">📺</span>
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">Presenter Mode</h3>
                    </div>
                    <Link
                        to={`/producer/event/${eventId}/presenter`}
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-green-600 text-white hover:bg-green-700 h-10 px-4 py-2"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Enter Presenter Mode
                    </Link>
                </div>
                <p className="text-muted-foreground">
                    Use presenter mode to display live results, current rider information, and event status on screens for spectators and participants.
                </p>
            </div>

            {/* Live Event Status */}
            <div className="rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                    <div className="h-6 w-6 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <span className="text-blue-600 text-sm">📊</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">Live Event Status</h3>
                </div>

                {eventStatus ? (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <span className="font-medium">Event Status:</span>
                            <span className={`px-2 py-1 rounded text-sm font-medium ${eventStatus.isRunning ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                {eventStatus.isRunning ? 'Running' : 'Not Started'}
                            </span>
                        </div>
                        {eventStatus.currentClass && (
                            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                <span className="font-medium">Current Class:</span>
                                <span className="text-sm">{eventStatus.currentClass.name}</span>
                            </div>
                        )}
                        {eventStatus.currentRider && (
                            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                <span className="font-medium">Current Rider:</span>
                                <span className="text-sm">{eventStatus.currentRider}</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-muted-foreground">Loading event status...</p>
                )}
            </div>
        </div>
    );
}

// Post Event Phase Component
function PostEventPhase({ event, eventId, classes, api }) {
    return (
        <div className="space-y-6">
            {/* Reports */}
            <div className="rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                    <div className="h-6 w-6 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <span className="text-blue-600 text-sm">📊</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">Event Reports</h3>
                </div>

                <Reports
                    api={api}
                    eventId={eventId}
                    classes={classes || []}
                    eventName={event?.name || 'Event'}
                />
            </div>

            {/* Event Webpage */}
            <div className="rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                    <div className="h-6 w-6 rounded-full bg-green-500/10 flex items-center justify-center">
                        <span className="text-green-600 text-sm">🌐</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">Event Webpage</h3>
                </div>

                <div className="space-y-4">
                    <p className="text-muted-foreground">
                        View a beautiful, printable webpage showing all classes and participants in the correct order.
                        Perfect for event organizers and spectators.
                    </p>

                    <Link
                        to={`/event/${eventId}/webpage`}
                        target="_blank"
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-green-600 text-white hover:bg-green-700 h-10 px-4 py-2"
                    >
                        🌐 View Event Webpage
                    </Link>
                </div>
            </div>
        </div>
    );
}

// A component to manage a single event
function EventManager({ eventId, api }) {
    const queryClient = useQueryClient();
    const [className, setClassName] = useState('');
    const [divisions, setDivisions] = useState([
        { id: '1D', enabled: true, timeSplit: 0 },
        { id: '2D', enabled: true, timeSplit: 0.5 },
        { id: '3D', enabled: true, timeSplit: 1.0 },
        { id: '4D', enabled: true, timeSplit: 1.5 },
        { id: '5D', enabled: true, timeSplit: 2.0 }
    ]);
    const [entryFee, setEntryFee] = useState(0);
    const [runData, setRunData] = useState({ participantId: '', horseName: '', time: '' });
    const [eventStateId, setEventStateId] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [toast, setToast] = useState(null);
    const [activePhase, setActivePhase] = useState('pre-event'); // 'pre-event', 'during-event', 'post-event'

    // Initialize event state when component mounts
    React.useEffect(() => {
        const initializeEventState = async () => {
            try {
                // Get or create the event state for this specific event
                const newEventStateId = await api.getOrCreateEventState(eventId);
                setEventStateId(newEventStateId);
            } catch (err) {
                console.error('Failed to initialize event state:', err);
                setToast({
                    message: 'Failed to initialize event state. Please try refreshing the page.',
                    type: 'error'
                });
            }
        };

        if (eventId) {
            initializeEventState();
        }
    }, [api, eventId]);

    // Fetch event details and classes
    const { data: event, isLoading: isLoadingEvent } = useQuery({
        queryKey: ['event', eventId],
        queryFn: () => api.getEvent(eventId),
    });

    // Fetch classes separately
    const { data: classes, isLoading: isLoadingClasses } = useQuery({
        queryKey: ['classes', eventId],
        queryFn: async () => {
            const classesData = await api.getClasses(eventId);
            // Fetch participant counts and class details for each class
            const classesWithDetails = await Promise.all(
                classesData.map(async (classInfo) => {
                    const [participants, classDetails] = await Promise.all([
                        api.getParticipants(classInfo.id),
                        api.getClassDetails(classInfo.id)
                    ]);
                    // Handle the case where entryFee might be an array
                    let entryFee = classDetails.entryFee || 0;
                    if (Array.isArray(entryFee)) {
                        entryFee = entryFee[1] || 0;
                    }

                    return {
                        ...classInfo,
                        participantCount: participants.length,
                        entryFee: entryFee
                    };
                })
            );
            return classesWithDetails;
        },
        enabled: !!eventId,
    });

    console.log('classes', classes)

    // Fetch event status to check if show is running
    const { data: eventStatus } = useQuery({
        queryKey: ['eventStatus', eventStateId],
        queryFn: () => api.getEventStatus(eventStateId),
        enabled: !!eventStateId,
    });

    // Function to copy spectator URL
    const copySpectatorUrl = () => {
        if (!eventStateId) return;

        const spectatorUrl = `${window.location.origin}/spectator/${eventStateId}`;
        navigator.clipboard.writeText(spectatorUrl).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000); // Reset success message after 2 seconds
        });
    };

    // Create class mutation
    const createClassMutation = useMutation({
        mutationFn: () => api.createClass(eventId, {
            name: className,
            divisions: divisions.filter(d => d.enabled).map(({ id, timeSplit }) => ({
                id,
                timeSplit
            })),
            entryFee: parseFloat(entryFee)
        }),
        onSuccess: () => {
            queryClient.invalidateQueries(['event', eventId]);
            queryClient.invalidateQueries(['eventStatus', eventStateId]);
            setClassName('');
            setToast({ message: 'Class created successfully!', type: 'success' });
        },
    });

    // Record run mutation
    const recordRunMutation = useMutation({
        mutationFn: (data) => api.recordRun('mock_class_id', { ...data, time: parseFloat(data.time) }),
        onSuccess: () => {
            queryClient.invalidateQueries(['event', eventId]);
            queryClient.invalidateQueries(['eventStatus', eventStateId]);
            queryClient.invalidateQueries(['participants']);
            setRunData({ participantId: '', horseName: '', time: '' });
            setToast({ message: 'Run recorded successfully!', type: 'success' });
        },
    });

    const handleCreateClass = async (e) => {
        e.preventDefault();
        if (!className) return;
        createClassMutation.mutate();
    };

    const handleRecordRun = async (e) => {
        e.preventDefault();
        if (!runData.participantId || !runData.horseName || !runData.time) return;
        recordRunMutation.mutate(runData);
    };

    const handleDivisionChange = (divisionId, field, value) => {
        setDivisions(prevDivisions =>
            prevDivisions.map(div =>
                div.id === divisionId
                    ? { ...div, [field]: value }
                    : div
            )
        );
    };

    if (isLoadingEvent) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Toast */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {/* Event Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-foreground">{event?.name}</h2>
                    <p className="text-muted-foreground">Event Management Dashboard</p>
                </div>
                <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">Event ID:</span>
                    <code className="px-2 py-1 bg-muted rounded text-sm">{eventId}</code>
                </div>
            </div>

            {/* Phase Navigation Tabs */}
            <div className="border-b">
                <nav className="flex space-x-8">
                    <button
                        onClick={() => setActivePhase('pre-event')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activePhase === 'pre-event'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center space-x-2">
                            <span>🎪</span>
                            <span>Pre-Event Setup</span>
                        </div>
                    </button>
                    <button
                        onClick={() => setActivePhase('during-event')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activePhase === 'during-event'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center space-x-2">
                            <span>🏇</span>
                            <span>During Event</span>
                        </div>
                    </button>
                    <button
                        onClick={() => setActivePhase('post-event')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activePhase === 'post-event'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center space-x-2">
                            <span>📊</span>
                            <span>Post-Event Reports</span>
                        </div>
                    </button>
                </nav>
            </div>

            {/* Phase Content */}
            {activePhase === 'pre-event' && (
                <PreEventPhase
                    event={event}
                    eventId={eventId}
                    eventStateId={eventStateId}
                    classes={classes}
                    isLoadingClasses={isLoadingClasses}
                    className={className}
                    setClassName={setClassName}
                    divisions={divisions}
                    setDivisions={setDivisions}
                    entryFee={entryFee}
                    setEntryFee={setEntryFee}
                    copySuccess={copySuccess}
                    copySpectatorUrl={copySpectatorUrl}
                    handleCreateClass={handleCreateClass}
                    handleDivisionChange={handleDivisionChange}
                    createClassMutation={createClassMutation}
                    api={api}
                    setToast={setToast}
                />
            )}

            {activePhase === 'during-event' && (
                <DuringEventPhase
                    event={event}
                    eventId={eventId}
                    eventStateId={eventStateId}
                    eventStatus={eventStatus}
                    api={api}
                    setToast={setToast}
                />
            )}

            {activePhase === 'post-event' && (
                <PostEventPhase
                    event={event}
                    eventId={eventId}
                    classes={classes}
                    api={api}
                />
            )}
        </div>
    );
}

// Class Management Modal Component
function ClassManagementModal({
    isOpen,
    onClose,
    classInfo,
    api,
    eventId,
    onSuccess
}) {
    const [activeTab, setActiveTab] = useState('participants'); // 'participants', 'draw', 'settings'
    const [participantData, setParticipantData] = useState({ name: '', homeTown: '', horseName: '' });
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [modalToast, setModalToast] = useState(null);
    const queryClient = useQueryClient();

    // Handle success/error messages within the modal
    const handleModalSuccess = (message, type = 'success') => {
        setModalToast({ message, type });
        // Also pass to parent for main page toast
        onSuccess?.(message, type);
    };

    // Fetch participants for the class
    const { data: participants, isLoading: isLoadingParticipants } = useQuery({
        queryKey: ['participants', classInfo?.id],
        queryFn: () => api.getParticipants(classInfo.id),
        enabled: !!classInfo?.id && isOpen,
    });

    // Create participant mutation
    const createParticipantMutation = useMutation({
        mutationFn: async () => {
            try {
                // First create the participant
                const participantId = await api.createParticipant({
                    name: participantData.name,
                    homeTown: participantData.homeTown,
                    horseName: participantData.horseName,
                });

                // Then add their horse
                await api.addHorseToParticipant(participantId, participantData.horseName);

                // Finally, add them to the class
                await api.addParticipantToClass(classInfo.id, participantId);

                return participantId;
            } catch (error) {
                console.error('Error in createParticipantMutation:', error);
                throw error;
            }
        },
        onSuccess: () => {
            // Invalidate all relevant queries to refresh the data
            queryClient.invalidateQueries(['participants', classInfo.id]);
            queryClient.invalidateQueries(['classes', eventId]);
            queryClient.invalidateQueries(['event', eventId]);
            queryClient.invalidateQueries(['eventStatus']);

            // Clear the form
            setParticipantData({ name: '', homeTown: '', horseName: '' });

            // Show success message
            handleModalSuccess('Participant added successfully!');
        },
        onError: (error) => {
            handleModalSuccess(`Failed to add participant: ${error.message}`, 'error');
        },
    });

    // Delete participant mutation
    const deleteParticipantMutation = useMutation({
        mutationFn: async (participantId) => {
            // Remove participant from all classes in the event
            await api.removeParticipantFromEvent(eventId, participantId);
            // Delete the participant
            await api.deleteParticipant(participantId);
        },
        onSuccess: () => {
            // Invalidate all relevant queries to refresh the data
            queryClient.invalidateQueries(['participants', classInfo.id]);
            queryClient.invalidateQueries(['classes', eventId]);
            queryClient.invalidateQueries(['event', eventId]);
            queryClient.invalidateQueries(['eventStatus']);
            setConfirmDelete(null);
            handleModalSuccess('Participant removed successfully!');
        },
        onError: (error) => {
            handleModalSuccess(`Failed to remove participant: ${error.message}`, 'error');
            setConfirmDelete(null);
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!participantData.name || !participantData.horseName) return;
        createParticipantMutation.mutate();
    };

    const handleDelete = (participant) => {
        setConfirmDelete(participant);
    };

    const confirmDeleteParticipant = () => {
        if (confirmDelete) {
            deleteParticipantMutation.mutate(confirmDelete.id);
        }
    };

    if (!isOpen || !classInfo) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div>
                        <h2 className="text-xl font-semibold text-foreground">{classInfo.name}</h2>
                        <p className="text-sm text-muted-foreground">
                            {participants?.length || 0} participants •
                            {(() => {
                                const fee = Array.isArray(classInfo.entryFee) ? classInfo.entryFee[1] || 0 : classInfo.entryFee;
                                return fee > 0 ? ` $${fee.toFixed(2)} entry fee` : ' Free entry';
                            })()}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Tab Navigation */}
                <div className="border-b">
                    <nav className="flex space-x-8 px-6">
                        <button
                            onClick={() => setActiveTab('participants')}
                            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'participants'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                                }`}
                        >
                            👥 Participants
                        </button>
                        <button
                            onClick={() => setActiveTab('draw')}
                            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'draw'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                                }`}
                        >
                            🎲 Draw Management
                        </button>
                        <button
                            onClick={() => setActiveTab('settings')}
                            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'settings'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                                }`}
                        >
                            ⚙️ Class Settings
                        </button>
                    </nav>
                </div>

                {/* Modal Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                    {/* Modal Toast */}
                    {modalToast && (
                        <div className={`mb-4 rounded-lg border p-4 ${modalToast.type === 'success'
                            ? 'border-green-500/50 bg-green-500/10'
                            : 'border-red-500/50 bg-red-500/10'
                            }`}>
                            <div className="flex items-center space-x-2">
                                <span className={modalToast.type === 'success' ? 'text-green-600' : 'text-red-600'}>
                                    {modalToast.type === 'success' ? '✅' : '⚠️'}
                                </span>
                                <p className={`text-sm ${modalToast.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                    {modalToast.message}
                                </p>
                                <button
                                    onClick={() => setModalToast(null)}
                                    className="ml-auto text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}
                    {/* Confirmation Dialog */}
                    {confirmDelete && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                                <h3 className="text-lg font-semibold mb-2">Confirm Removal</h3>
                                <p className="text-muted-foreground mb-4">
                                    Are you sure you want to remove {confirmDelete.name} from this event?
                                    This will remove them from all registered classes and cannot be undone.
                                </p>
                                <div className="flex justify-end space-x-2">
                                    <button
                                        onClick={() => setConfirmDelete(null)}
                                        className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 hover:bg-gray-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmDeleteParticipant}
                                        disabled={deleteParticipantMutation.isPending}
                                        className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700"
                                    >
                                        {deleteParticipantMutation.isPending ? 'Removing...' : 'Remove'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Participants Tab */}
                    {activeTab === 'participants' && (
                        <div className="space-y-6">
                            {/* Add Participant Form */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-lg font-semibold mb-4">Add New Participant</h3>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <label htmlFor="name" className="text-sm font-medium text-foreground">
                                                Participant Name
                                            </label>
                                            <input
                                                id="name"
                                                value={participantData.name}
                                                onChange={e => setParticipantData({ ...participantData, name: e.target.value })}
                                                placeholder="e.g., John Doe"
                                                disabled={createParticipantMutation.isPending}
                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="homeTown" className="text-sm font-medium text-foreground">
                                                Home Town
                                            </label>
                                            <input
                                                id="homeTown"
                                                value={participantData.homeTown}
                                                onChange={e => setParticipantData({ ...participantData, homeTown: e.target.value })}
                                                placeholder="e.g., Austin, TX"
                                                disabled={createParticipantMutation.isPending}
                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="horseName" className="text-sm font-medium text-foreground">
                                                Horse Name
                                            </label>
                                            <input
                                                id="horseName"
                                                value={participantData.horseName}
                                                onChange={e => setParticipantData({ ...participantData, horseName: e.target.value })}
                                                placeholder="e.g., Lightning"
                                                disabled={createParticipantMutation.isPending}
                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={createParticipantMutation.isPending || !participantData.name || !participantData.horseName}
                                        className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                                    >
                                        {createParticipantMutation.isPending ? (
                                            <>
                                                <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Adding...
                                            </>
                                        ) : (
                                            'Add Participant'
                                        )}
                                    </button>
                                </form>
                            </div>

                            {/* Participants List */}
                            <div>
                                <h3 className="text-lg font-semibold mb-4">Current Participants</h3>
                                {isLoadingParticipants ? (
                                    <div className="flex items-center justify-center p-4">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                                    </div>
                                ) : participants?.length > 0 ? (
                                    <div className="space-y-2">
                                        {participants.map((participant) => (
                                            <div
                                                key={participant.id}
                                                className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white"
                                            >
                                                <div>
                                                    <h4 className="font-medium text-foreground">{participant.name}</h4>
                                                    <p className="text-sm text-muted-foreground">
                                                        {[participant.homeTown, participant.horses?.[0]].filter(Boolean).join(' • ')}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => handleDelete(participant)}
                                                    className="px-3 py-1 rounded text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                                        <div className="text-gray-400 mb-2">👥</div>
                                        <p className="text-muted-foreground">No participants in this class yet</p>
                                        <p className="text-sm text-muted-foreground">Add participants using the form above</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Draw Management Tab */}
                    {activeTab === 'draw' && (
                        <div>
                            <h3 className="text-lg font-semibold mb-4">Draw Management</h3>
                            <DrawManager
                                api={api}
                                classId={classInfo.id}
                                className={classInfo.name}
                            />
                        </div>
                    )}

                    {/* Class Settings Tab */}
                    {activeTab === 'settings' && (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold mb-4">Class Settings</h3>

                            <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="font-medium mb-3">Class Information</h4>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Class Name:</span>
                                        <span className="font-medium">{classInfo.name}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Entry Fee:</span>
                                        <span className="font-medium">
                                            {(() => {
                                                const fee = Array.isArray(classInfo.entryFee) ? classInfo.entryFee[1] || 0 : classInfo.entryFee;
                                                return fee > 0 ? `$${fee.toFixed(2)}` : 'Free';
                                            })()}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Participants:</span>
                                        <span className="font-medium">{participants?.length || 0}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-start space-x-2">
                                    <span className="text-yellow-600">⚠️</span>
                                    <div>
                                        <h4 className="font-medium text-yellow-800">Class Settings</h4>
                                        <p className="text-sm text-yellow-700 mt-1">
                                            Class name and entry fee settings cannot be modified after creation.
                                            To change these settings, you would need to create a new class.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export function ProducerView({ api }) {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [eventName, setEventName] = useState('');
    const [dragsEveryNRuns, setDragsEveryNRuns] = useState(0);

    // Create event mutation
    const createEventMutation = useMutation({
        mutationFn: () => api.createEvent({
            name: eventName,
            dragsEveryNRuns: parseInt(dragsEveryNRuns, 10)
        }),
        onSuccess: (newEventId) => {
            queryClient.invalidateQueries(['events']);
            navigate(`/producer/event/${newEventId}`);
        },
    });

    const handleCreateEvent = async (e) => {
        e.preventDefault();
        if (!eventName) return;
        createEventMutation.mutate();
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <span className="text-primary text-lg">🎯</span>
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-foreground">Producer Dashboard</h2>
                    <p className="text-muted-foreground">Manage events, classes, and record runs</p>
                </div>
            </div>

            {/* Error Alert */}
            {createEventMutation.isError && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                    <div className="flex items-center space-x-2">
                        <span className="text-destructive">⚠️</span>
                        <p className="text-sm text-destructive">Failed to create event: {createEventMutation.error.message}</p>
                    </div>
                </div>
            )}

            {!eventId ? (
                <div className="space-y-8">
                    {/* Create Event Form */}
                    <div className="rounded-lg border bg-card p-6 shadow-sm">
                        <div className="flex items-center space-x-2 mb-4">
                            <div className="h-6 w-6 rounded-full bg-purple-500/10 flex items-center justify-center">
                                <span className="text-purple-600 text-sm">🎪</span>
                            </div>
                            <h3 className="text-lg font-semibold text-foreground">Create a New Event</h3>
                        </div>

                        <form onSubmit={handleCreateEvent} className="space-y-4">
                            <div className="space-y-2">
                                <label htmlFor="eventName" className="text-sm font-medium text-foreground">
                                    Event Name
                                </label>
                                <input
                                    id="eventName"
                                    type="text"
                                    value={eventName}
                                    onChange={e => setEventName(e.target.value)}
                                    placeholder="e.g., Spring Barrel Racing Championship 2024"
                                    disabled={createEventMutation.isPending}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                />
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="dragsEveryNRuns" className="text-sm font-medium text-foreground">
                                    Arena Drag Interval
                                </label>
                                <div className="flex items-center space-x-2">
                                    <input
                                        id="dragsEveryNRuns"
                                        type="number"
                                        min="0"
                                        value={dragsEveryNRuns}
                                        onChange={e => setDragsEveryNRuns(e.target.value)}
                                        placeholder="0"
                                        disabled={createEventMutation.isPending}
                                        className="flex h-10 w-32 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    />
                                    <span className="text-sm text-muted-foreground">runs between drags (0 = no drags)</span>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={createEventMutation.isPending || !eventName}
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                            >
                                {createEventMutation.isPending ? (
                                    <>
                                        <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Creating...
                                    </>
                                ) : (
                                    'Create Event'
                                )}
                            </button>
                        </form>
                    </div>

                    {/* Event List */}
                    <EventList api={api} />
                </div>
            ) : (
                <EventManager eventId={eventId} api={api} />
            )}
        </div>
    );
}